import React, { useEffect, useState } from 'react';
import { FiSend, FiUsers, FiUser, FiBriefcase, FiHeart, FiCheck, FiShield } from 'react-icons/fi';
import { Card, Button, Input, Badge, Table, Pagination } from '../../components/UI';
import { usePagination } from '../../hooks';
import {
  sendAdminNotification,
  getNotificationHistory,
  notificationTemplates,
} from '../../services/notificationService';
import styles from './Notifications.module.css';

const targetOptions = [
  { value: 'broadcast', label: 'All Users (Broadcast)', icon: FiUsers },
  { value: 'role:business', label: 'Verified Businesses', icon: FiBriefcase },
  { value: 'role:vet', label: 'Verified Vets', icon: FiHeart },
  { value: 'role:admin', label: 'Admins Only', icon: FiShield },
  { value: 'user', label: 'Specific Users', icon: FiUser },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const targetLabel = (target) => {
  if (!target) return '-';
  if (target.type === 'broadcast' || target.type === 'all') return 'All users';
  if (target.type === 'role') return `Role: ${target.role}`;
  if (target.type === 'user') return `${(target.userIds || []).length} user(s)`;
  return target.type;
};

const Notifications = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetKey, setTargetKey] = useState('broadcast');
  const [userIds, setUserIds] = useState('');
  // Transactional = operational message (security alert, account update,
  // refund). These bypass the marketing-opt-out filter and the GDPR Art. 18
  // restriction filter because they're categorically not marketing — the
  // user can't opt out of being told their account got banned. Default OFF
  // so the conservative path applies unless the admin explicitly opts in.
  const [transactional, setTransactional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const refreshHistory = async () => {
    setHistoryLoading(true);
    try {
      const items = await getNotificationHistory(100);
      setHistory(items);
    } catch (err) {
      console.error(err);
      setError(`Failed to load history: ${err.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    refreshHistory();
  }, []);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(history, 10);

  const handleTemplateSelect = (key) => {
    setTitle(notificationTemplates[key].title);
    setBody(notificationTemplates[key].body);
  };

  const handleSend = async () => {
    if (!title || !body) {
      setError('Please fill in both title and body');
      return;
    }

    let target;
    if (targetKey === 'broadcast') {
      target = { type: 'broadcast' };
    } else if (targetKey.startsWith('role:')) {
      target = { type: 'role', role: targetKey.split(':')[1] };
    } else if (targetKey === 'user') {
      const ids = userIds
        .split(/[\s,]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      if (!ids.length) {
        setError('Enter at least one user UID');
        return;
      }
      target = { type: 'user', userIds: ids };
    }

    try {
      setLoading(true);
      setError('');
      setSuccess(null);
      const result = await sendAdminNotification({ title, body, target, transactional });
      setSuccess(
        `Sent! Recipients: ${result.recipientCount ?? 0}, Delivered: ${result.okCount ?? 0}, Failed: ${result.errorCount ?? 0}`
      );
      setTitle('');
      setBody('');
      setUserIds('');
      refreshHistory();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error(err);
      setError(`Failed to send: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    { key: 'title', title: 'Title' },
    {
      key: 'body',
      title: 'Body',
      render: (value) => (
        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {value}
        </span>
      ),
    },
    { key: 'target', title: 'Target', render: (value) => targetLabel(value) },
    {
      key: 'recipientCount',
      title: 'Recipients',
      render: (value) => value ?? 0,
    },
    {
      key: 'okCount',
      title: 'Delivered',
      render: (value, row) => (
        <span>
          <Badge variant={row.errorCount ? 'warning' : 'success'}>
            {value ?? 0}/{row.deliverableCount ?? 0}
          </Badge>
        </span>
      ),
    },
    { key: 'sentByEmail', title: 'Sender' },
    { key: 'createdAt', title: 'Sent', render: (value) => formatDate(value) },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Push Notifications</h1>
        <p className={styles.subtitle}>Send announcements to app users</p>
      </div>

      <div className={styles.grid}>
        <Card title="Compose Notification" className={styles.composeCard}>
          {success && (
            <div className={styles.success}>
              <FiCheck />
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <span>{error}</span>
            </div>
          )}

          <div className={styles.form}>
            <Input
              label="Notification Title"
              placeholder="Enter title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <div className={styles.textAreaGroup}>
              <label className={styles.label}>Message Body</label>
              <textarea
                className={styles.textArea}
                placeholder="Enter message..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
              />
            </div>

            <div className={styles.targetGroup}>
              <label className={styles.label}>Target Audience</label>
              <div className={styles.targetOptions}>
                {targetOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`${styles.targetBtn} ${targetKey === option.value ? styles.active : ''}`}
                    onClick={() => setTargetKey(option.value)}
                  >
                    <option.icon />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {targetKey === 'user' && (
              <Input
                label="User UIDs (comma or whitespace separated)"
                placeholder="abc123, def456, ..."
                value={userIds}
                onChange={(e) => setUserIds(e.target.value)}
              />
            )}

            {/* GDPR Art. 21 + ePrivacy: marketing messages must respect
                opt-out. Transactional messages (security alerts, account
                updates, refunds) are categorically not marketing and bypass
                the filter. Default OFF so the conservative path applies. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '12px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={transactional}
                onChange={(e) => setTransactional(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ color: '#e8eaed', fontSize: 14, fontWeight: 500 }}>
                  Transactional / operational message
                </div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>
                  Bypasses marketing opt-out and Art. 18 restriction filters.
                  ONLY tick this for non-marketing communications: security alerts,
                  account decisions, billing updates, legally-required notices.
                  Misusing this flag for marketing is a GDPR / ePrivacy violation.
                </div>
              </div>
            </label>

            <Button
              variant="primary"
              size="lg"
              icon={<FiSend />}
              onClick={handleSend}
              loading={loading}
              disabled={!title || !body}
              fullWidth
            >
              Send Notification
            </Button>
          </div>
        </Card>

        <Card title="Quick Templates" className={styles.templatesCard}>
          <div className={styles.templates}>
            {Object.entries(notificationTemplates).map(([key, template]) => (
              <button
                key={key}
                className={styles.templateBtn}
                onClick={() => handleTemplateSelect(key)}
              >
                <div className={styles.templateContent}>
                  <span className={styles.templateTitle}>{template.title}</span>
                  <span className={styles.templateBody}>{template.body}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Recent Notifications"
        subtitle={`${history.length} sent`}
        className={styles.historyCard}
        action={
          <Button variant="ghost" size="sm" onClick={refreshHistory} disabled={historyLoading}>
            Refresh
          </Button>
        }
      >
        <Table
          columns={historyColumns}
          data={pageData}
          loading={historyLoading}
          emptyMessage="No notifications sent yet"
        />
        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 25, 50]}
        />
      </Card>
    </div>
  );
};

export default Notifications;
